/* iter55m — Email Provider Health Banner.
 *
 * Polls /api/admin/email-provider-health once at mount and every 5 min.
 * Renders a red banner when the currently configured EMAIL_PROVIDER is
 * unreachable so silent email outages surface immediately.
 */
import React, { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { adminApi } from '@/lib/api';

export default function EmailProviderHealthBanner() {
  const [state, setState] = useState({ loading: true });
  const check = async () => {
    try {
      const r = await adminApi.emailProviderHealth();
      setState({ loading: false, ...r });
    } catch (e) {
      setState({ loading: false, healthy: false,
                 provider: 'unknown',
                 note: e?.response?.data?.detail || 'Health check failed.' });
    }
  };
  useEffect(() => {
    check();
    const t = window.setInterval(check, 5 * 60_000);
    return () => window.clearInterval(t);
  }, []);

  if (state.loading) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-1.5 text-[11.5px] text-muted-foreground"
           data-testid="email-provider-health-loading">
        <Loader2 className="h-3 w-3 animate-spin" />
        Checking email provider…
      </div>
    );
  }
  // Only surface the banner when unhealthy — healthy state stays silent
  // to reduce chrome noise on the admin hub.
  if (state.healthy) {
    return (
      <div className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10.5px] text-emerald-700 dark:text-emerald-400"
           data-testid="email-provider-health-ok"
           title={state.note}>
        <CheckCircle2 className="h-3 w-3" />
        Email · {state.provider}
      </div>
    );
  }
  return (
    <div className="rounded-md border border-rose-500/50 bg-rose-500/10 px-3 py-2 text-[12px] text-rose-900 dark:text-rose-200"
         data-testid="email-provider-health-warn">
      <div className="flex items-center gap-2 font-semibold">
        <AlertTriangle className="h-3.5 w-3.5" />
        Email provider `{state.provider}` is UNREACHABLE
      </div>
      <div className="mt-0.5 pl-5 text-[11px] opacity-90">
        {state.note} — outbound emails will fail until this is resolved.
        {' '}
        <button type="button" onClick={check}
                className="underline hover:no-underline"
                data-testid="email-provider-health-retry">
          Retry check
        </button>
        {' · '}
        <Link to="/admin/operations?tab=keys" className="underline hover:no-underline" data-testid="email-provider-open-settings">Open Resend toggle</Link>
      </div>
    </div>
  );
}
